import {
  Attributes,
  AttributesRow,
  Button,
  ButtonGroup,
  Card,
  DataStatus,
  DialogFooter,
  Flag,
  Separator,
  Table,
  TableRow,
  Tabs,
} from 'platform/components';
import {Box, Grid, Heading, Show, ThemeColorTextPath, VStack} from 'platform/foundation';
import {useFormatCurrency, useFormatNumber} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {always, isNil, isNotNil} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetServiceOrderIntegrationsMazdaPreauthorizationDetailQuery} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {DEFAULT_CURRENCY} from '@dms/shared';

import {buildArray, Nullish, suffixTestId, TestIdProps} from 'shared/web';

import {getExternalStatusLabel} from '../utils/getExternalStatusLabel';
import {getMazdaAmountAgreementState} from '../utils/getMazdaAmountAgreementState';
import {getMazdaItemTypeFlag} from '../utils/getMazdaItemTypeFlag';
import {getMazdaScopeLabel} from '../utils/getMazdaScopeLabel';
import {getMazdaStatusColorScheme} from '../utils/getMazdaStatusColorScheme';
import {getMazdaWarrantyTypeLabel} from '../utils/getMazdaWarrantyTypeLabel';
import {sortMazdaDecisionItemsBySequenceNumber} from '../utils/sortMazdaDecisionItemsBySequenceNumber';
import {MazdaDetailOverviewAmountCell} from './MazdaDetailOverviewAmountCell';
import {MazdaDetailTableHeaderCell} from './MazdaDetailTableHeaderCell';
import {MazdaDetailTableTextCell} from './MazdaDetailTableTextCell';

const GENERAL_TAB = 'GENERAL';
const DECISIONS_TAB = 'DECISIONS';

interface MazdaPreauthDetailDialogProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  viewInMazdaUrl?: string | Nullish;
  onClose: () => void;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function MazdaPreauthDetailDialog(props: MazdaPreauthDetailDialogProps) {
  const [activeTabId, setActiveTabId] = useState(GENERAL_TAB);
  const formatNumber = useFormatNumber();
  const formatCurrency = useFormatCurrency();

  const {data: {currency: defaultCurrency} = {}} = useGetTenantQuery();

  const {data, isLoading, isError} = useGetServiceOrderIntegrationsMazdaPreauthorizationDetailQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {refetchOnMountOrArgChange: true}
  );

  const formatAmount = (amount: number | Nullish) =>
    isNotNil(amount)
      ? (formatCurrency(amount, defaultCurrency ?? DEFAULT_CURRENCY, 2) ?? undefined)
      : undefined;

  const decisionItems = sortMazdaDecisionItemsBySequenceNumber(data?.decision?.items ?? []);

  const overviewCells = buildArray<{
    key: string;
    label: string;
    requestedAmount: number | Nullish;
    agreedAmount: number | Nullish;
  }>()
    .add({
      key: 'total',
      label: i18n.t('entity.orderIntegration.labels.totalAgreedAmount'),
      requestedAmount: data?.decision?.overall?.totalRequestedAmount,
      agreedAmount: data?.decision?.overall?.totalAgreedAmount,
    })
    .add({
      key: 'parts',
      label: i18n.t('entity.orderIntegration.labels.agreedAmountParts'),
      requestedAmount: data?.decision?.overall?.totalRequestedAmountParts,
      agreedAmount: data?.decision?.overall?.totalAgreedAmountParts,
    })
    .add({
      key: 'labour',
      label: i18n.t('entity.orderIntegration.labels.agreedAmountLabour'),
      requestedAmount: data?.decision?.overall?.totalRequestedAmountLabour,
      agreedAmount: data?.decision?.overall?.totalAgreedAmountLabour,
    })
    .add({
      key: 'sublets',
      label: i18n.t('entity.orderIntegration.labels.agreedAmountSublets'),
      requestedAmount: data?.decision?.overall?.totalRequestedAmountSublets,
      agreedAmount: data?.decision?.overall?.totalAgreedAmountSublets,
    });

  return (
    <DataStatus isLoading={isLoading} isError={isError} minHeight={100}>
      <Tabs
        variant="condensed"
        activeTabId={activeTabId}
        onChange={setActiveTabId}
        data-testid={suffixTestId('tabs', props)}
        tabs={[
          {
            id: GENERAL_TAB,
            title: i18n.t('entity.integration.labels.generalInformation'),
            content: (
              <VStack spacing={4}>
                <Attributes
                  size="quarter"
                  data-testid={suffixTestId('generalAttributes', props)}
                  rows={buildArray<AttributesRow>()
                    .whenNotNil(data?.externalStatus, (status) => ({
                      label: i18n.t('entity.claimType.labels.state'),
                      content: (
                        <Flag
                          size="small"
                          label={getExternalStatusLabel(status)}
                          colorScheme={getMazdaStatusColorScheme(status)}
                          data-testid={suffixTestId('statusFlag', props)}
                        />
                      ),
                    }))
                    .add({
                      label: i18n.t('general.labels.number'),
                      value: data?.externalId,
                    })
                    .add({
                      label: i18n.t('entity.orderIntegration.labels.scope'),
                      value: getMazdaScopeLabel(data?.scope),
                    })
                    .add({
                      label: i18n.t('entity.orderIntegration.labels.warrantyType'),
                      value: getMazdaWarrantyTypeLabel(data?.warrantyType),
                    })
                    .add({
                      label: i18n.t('entity.integration.labels.mileage'),
                      value: isNotNil(data?.mileage)
                        ? `${formatNumber(data?.mileage, 0)} ${i18n.t('general.metric.km')}`
                        : undefined,
                    })
                    .add({
                      label: i18n.t(
                        'entity.orderIntegration.labels.vehicleServicedAccordingToSpecs'
                      ),
                      value: match(data?.vehicleServicedAccordingToManufacturerSpecs)
                        .with(true, always(i18n.t('general.labels.yes')))
                        .with(false, always(i18n.t('general.labels.no')))
                        .otherwise(always(undefined)),
                    })
                    .add({
                      label: i18n.t('entity.orderIntegration.labels.causalPart'),
                      value: data?.causalPart,
                    })
                    .add({
                      label: i18n.t('general.labels.message'),
                      value: data?.message,
                    })}
                />
                <Separator spacing={0} />
                <Heading size={4}>
                  {i18n.t('entity.orderIntegration.labels.assessmentCodes')}
                </Heading>
                <DataStatus isEmpty={isNilOrEmpty(data?.assessmentCodes)} minHeight={30}>
                  <Table data-testid={suffixTestId('assessmentCodesTable', props)}>
                    <TableRow color="palettes.neutral.10.100">
                      <MazdaDetailTableHeaderCell label={i18n.t('entity.warehouse.labels.code')} />
                      <MazdaDetailTableHeaderCell label={i18n.t('general.labels.description')} />
                      <MazdaDetailTableHeaderCell
                        label={i18n.t('general.labels.additionalInformation')}
                        isLast
                      />
                    </TableRow>
                    {data?.assessmentCodes?.map((assessmentCode, index) => (
                      <TableRow
                        key={`${assessmentCode?.code}-${assessmentCode?.additionalInformation}`}
                        data-testid={suffixTestId(`assessmentCode-[${index}]`, props)}
                      >
                        <MazdaDetailTableTextCell value={assessmentCode?.code} />
                        <MazdaDetailTableTextCell value={assessmentCode?.description} />
                        <MazdaDetailTableTextCell value={assessmentCode?.additionalInformation} />
                      </TableRow>
                    ))}
                  </Table>
                </DataStatus>
              </VStack>
            ),
          },
          {
            id: DECISIONS_TAB,
            title: i18n.t('entity.orderIntegration.labels.decisions'),
            content: (
              <DataStatus isEmpty={isNil(data?.decision)} minHeight={50}>
                <VStack spacing={4}>
                  <Card title={i18n.t('general.labels.overview')} variant="inlineGrey">
                    <Grid columns={4} spacing={4}>
                      {overviewCells.map((cell) => (
                        <MazdaDetailOverviewAmountCell
                          key={cell.key}
                          label={cell.label}
                          requestedAmount={cell.requestedAmount}
                          agreedAmount={cell.agreedAmount}
                          formatAmount={formatAmount}
                          data-testid={suffixTestId(`overview-${cell.key}`, props)}
                        />
                      ))}
                    </Grid>
                  </Card>
                  <Heading size={4}>{i18n.t('entity.orderRequest.labels.workItems')}</Heading>
                  <DataStatus isEmpty={isNilOrEmpty(decisionItems)} minHeight={30}>
                    <Table data-testid={suffixTestId('workItemsTable', props)}>
                      <TableRow color="palettes.neutral.10.100">
                        <MazdaDetailTableHeaderCell
                          label={i18n.t('entity.warehouse.labels.catalogNumber')}
                        />
                        <MazdaDetailTableHeaderCell label={i18n.t('general.labels.type')} />
                        <MazdaDetailTableHeaderCell
                          label={i18n.t('entity.orderIntegration.labels.requestedQuantity')}
                          align="right"
                        />
                        <MazdaDetailTableHeaderCell
                          label={i18n.t('entity.orderIntegration.labels.agreedQuantity')}
                          align="right"
                        />
                        <MazdaDetailTableHeaderCell
                          label={i18n.t('entity.orderIntegration.labels.requestedAmount')}
                          align="right"
                        />
                        <MazdaDetailTableHeaderCell
                          label={i18n.t('entity.orderIntegration.labels.agreedAmount')}
                          align="right"
                          isLast
                        />
                      </TableRow>
                      {decisionItems.map((item, index) => {
                        const typeFlag = getMazdaItemTypeFlag(item?.type);
                        const agreementState = getMazdaAmountAgreementState(
                          item?.requestedAmount,
                          item?.agreedAmount
                        );
                        const agreedAmountColor = match<
                          typeof agreementState,
                          ThemeColorTextPath | undefined
                        >(agreementState)
                          .with('agreed', always('success'))
                          .with('notAgreed', always('danger'))
                          .otherwise(always(undefined));

                        return (
                          <TableRow
                            key={`${item?.catalogNumber}-${item?.type}-${item?.sequenceNumber}`}
                            data-testid={suffixTestId(`workItem-[${index}]`, props)}
                          >
                            <MazdaDetailTableTextCell value={item?.catalogNumber} />
                            <Box paddingHorizontal={4} paddingVertical={2}>
                              <Show when={isNotNil(typeFlag)}>
                                <Flag
                                  isSubtle
                                  size="small"
                                  label={typeFlag?.label ?? ''}
                                  colorScheme={typeFlag?.colorScheme}
                                />
                              </Show>
                            </Box>
                            <MazdaDetailTableTextCell
                              value={
                                isNotNil(item?.requestedQuantity)
                                  ? formatNumber(item?.requestedQuantity)
                                  : undefined
                              }
                              align="right"
                            />
                            <MazdaDetailTableTextCell
                              value={
                                isNotNil(item?.agreedQuantity)
                                  ? formatNumber(item?.agreedQuantity)
                                  : undefined
                              }
                              align="right"
                            />
                            <MazdaDetailTableTextCell
                              value={formatAmount(item?.requestedAmount)}
                              align="right"
                            />
                            <MazdaDetailTableTextCell
                              value={formatAmount(item?.agreedAmount)}
                              color={agreedAmountColor}
                              align="right"
                            />
                          </TableRow>
                        );
                      })}
                    </Table>
                  </DataStatus>
                </VStack>
              </DataStatus>
            ),
          },
        ]}
      />
      <DialogFooter>
        <ButtonGroup align="right">
          <Show when={isNotNilOrEmpty(props.viewInMazdaUrl)}>
            <Button
              title={i18n.t('entity.orderIntegration.actions.viewInMazda')}
              variant="outlined"
              rightIcon="action/open_in_new"
              onClick={() =>
                window.open(props.viewInMazdaUrl ?? '', '_blank', 'noopener,noreferrer')
              }
              data-testid={suffixTestId('viewInMazda', props)}
            />
          </Show>
          <Button
            title={i18n.t('general.actions.close')}
            variant="secondary"
            onClick={props.onClose}
            data-testid={suffixTestId('close', props)}
          />
        </ButtonGroup>
      </DialogFooter>
    </DataStatus>
  );
}
